import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Adaptive ₣ formatter for FIXED-WIDTH displays (rankings, mini-standings,
 * stat pills) so millions/billions never overflow their box. Mirrors the
 * mobile `formatFrancAdaptive` in `mobile/src/lib/constants.js`.
 *   < 1M  → full commas ("₣267,937")   ≥ 1M → "₣1.2M"   ≥ 1B → "₣3.4B"
 */
export function formatFrancAdaptive(amount, opts = {}) {
  const { decimals = 0, symbol = true } = opts;
  const v = Number(amount) || 0;
  const abs = Math.abs(v);
  // "₣5,001" / "-₣12" — no space after ₣ (owner, 2-Nov 2026).
  const neg = v < 0 && Number(abs.toFixed(decimals)) > 0;
  const pre = `${neg ? '-' : ''}${symbol ? '₣' : ''}`;
  const compact = (n, suffix) => `${pre}${n.toFixed(1).replace(/\.0$/, '')}${suffix}`;
  if (abs >= 1_000_000_000) return compact(abs / 1_000_000_000, 'B');
  if (abs >= 1_000_000) return compact(abs / 1_000_000, 'M');
  return `${pre}${abs.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/** Money with pennies — prices, budget, fees, earnings: "₣1,749.65"; sign → "+₣0.15". */
export function formatFranc(amount, opts = {}) {
  const { decimals = 2, sign = false } = opts;
  const r = Number((Number(amount) || 0).toFixed(decimals));
  const s = r < 0 ? '-' : (sign && r > 0 ? '+' : '');
  return `${s}₣${Math.abs(r).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/**
 * 2026-07-21 (owner request) — paper gain/loss across a roster: sum of
 * (current price − acquired price) over slots the viewer bought. Mirrors
 * mobile's `computePortfolioDelta` in `mobile/src/lib/constants.js`.
 * Returns null when no slot carries an acquired_price (nothing bought yet,
 * or viewing a roster where acquisition data is private).
 */
export function computePortfolioDelta(slots) {
  let invested = 0;
  let current = 0;
  let count = 0;
  for (const s of slots || []) {
    if (!s || s._isPlaceholder || s.acquired_price == null) continue;
    invested += s.acquired_price;
    current += s.entity?.pricing?.price || 0;
    count += 1;
  }
  if (!count) return null;
  return { invested, current, delta: Math.round((current - invested) * 100) / 100, count };
}
